import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";
import { ImportProspection } from "@/components/prospection/ImportProspection";
import { ChoixListe } from "@/pages/parametres/ChoixListe";
import { confirmer } from "@/lib/confirmer";

const Pill = ({ actif, onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="min-h-[44px] px-4 at-pill font-semibold whitespace-nowrap"
    style={{ background: actif ? "var(--accent)" : "var(--surface)", color: actif ? "var(--accent-ink)" : "var(--ink)" }}>{children}</button>
);

const Chargement = ({ erreur, testId }) => <p className="at-muted" data-testid={`${testId}-${erreur ? "error" : "loading"}`}>{erreur || "Chargement…"}</p>;

const Fichiers = ({ cle }) => {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState(null);
  const charger = useCallback(() => api.get("/prospection/fichiers").then((r) => setListe(r.data)).catch((e) => setErr(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger, cle]);
  const suppr = (f) => confirmer(`Mettre « ${f.nom} » et ses contacts à la corbeille ?`).then((ok) => ok && api.delete(`/prospection/fichiers/${f.id}`).then(() => { toast.success("Fichier mis à la corbeille."); charger(); }).catch((e) => toast.error(messageErreur(e))));
  if (!liste) return <Chargement erreur={err} testId="files" />;
  if (!liste.length) return <Vide texte="Aucun fichier de prospection." testId="files-empty" />;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="files-list">
      {liste.map((f) => (
        <section key={f.id} className="at-card p-4 space-y-3" data-testid={`file-card-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0"><p className="font-bold truncate">{f.nom}</p><p className="text-xs at-muted">{formatDateHeure(f.created_at)}{f.type === "recyclage" ? " · Recyclage" : f.type === "auto" ? " · Import automatique" : ""}</p></div>
            <button type="button" className="at-icon-btn shrink-0" aria-label="Corbeille" onClick={() => suppr(f)} data-testid={`file-trash-${f.id}`}><Trash2 size={16} /></button>
          </div>
          <div className="flex h-3 at-pill overflow-hidden" style={{ background: "var(--surface-2)" }} data-testid={`file-bar-${f.id}`}>
            {f.repartition.map((r) => <span key={r.code} style={{ flex: r.nombre, background: r.couleur }} title={`${r.nom} : ${r.nombre}`} />)}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {[["Contacts", f.nb_contacts, "count"], ["À contacter", f.a_contacter, "todo"], ["Intéressés", f.interesses, "won"], ["À rappeler", f.a_rappeler, "callback"], ["Tentatives moy.", f.tentatives_moyennes, "tries"], ["Agents", f.agents.length, "agents"]].map(([l, v, t]) => (
              <div key={t} className="at-soft p-2"><p className="at-muted">{l}</p><p className="font-bold text-base" data-testid={`file-${t}-${f.id}`}>{v}</p></div>
            ))}
          </div>
          {f.agents.length > 0 && <p className="text-xs at-muted truncate">{f.agents.join(", ")}</p>}
        </section>
      ))}
    </div>
  );
};

const Distribution = () => {
  const [d, setD] = useState(null);
  const [adj, setAdj] = useState({});
  const [err, setErr] = useState(null);
  const charger = useCallback(() => api.get("/prospection/distribution").then((r) => setD(r.data)).catch((e) => setErr(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const relancer = () => api.post("/prospection/distribution/relancer", { ajustements: Object.keys(adj).length ? adj : null })
    .then((r) => { toast.success(`Distribution faite : ${r.data.plan.reduce((s, p) => s + p.a_recevoir, 0)} contact(s).`); setAdj({}); charger(); }).catch((e) => toast.error(messageErreur(e)));
  const dose = (id, v) => api.patch(`/prospection/agents/${id}`, { dose_jour: Number(v) }).then(charger).catch((e) => toast.error(messageErreur(e)));
  if (!d) return <Chargement erreur={err} testId="dist" />;
  return (
    <div className="space-y-3" data-testid="distribution-view">
      <p className="at-muted text-sm">Stock disponible : <b data-testid="dist-stock">{d.stock_disponible}</b>{d.derniere ? ` · Dernière distribution ${formatDateHeure(d.derniere.date)} (${d.derniere.par})` : ""}</p>
      {d.plan.map((p) => (
        <div key={p.agent_id} className="at-card p-4 space-y-2" data-testid={`dist-agent-${p.agent_id}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-bold">{p.agent}</p><p className="text-sm font-bold" data-testid={`dist-receive-${p.agent_id}`}>+{p.a_recevoir}</p></div>
          <p className="text-xs at-muted">Déjà en liste : {p.deja_en_liste} · frais : {p.frais} · {Object.entries(p.regions).map(([r, n]) => `${r} ${n}`).join(", ") || "aucune région"}</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="at-label block mb-1">Dose habituelle</span><input key={p.dose} type="number" min="0" defaultValue={p.dose} onBlur={(e) => Number(e.target.value) !== p.dose && dose(p.agent_id, e.target.value)} className="at-input" data-testid={`dist-dose-${p.agent_id}`} /></label>
            <label className="block"><span className="at-label block mb-1">Aujourd'hui seulement</span><input type="number" min="0" value={adj[p.agent_id] ?? ""} onChange={(e) => setAdj({ ...adj, [p.agent_id]: e.target.value })} placeholder={String(p.dose)} className="at-input" data-testid={`dist-adjust-${p.agent_id}`} /></label>
          </div>
          <p className="at-label">Objectifs du jour</p>
          <div className="grid grid-cols-3 gap-2">
            {[["objectif_appels", "Appels"], ["objectif_decroches", "Décrochés"], ["objectif_opportunites", "Opport."]].map(([k, l]) => {
              const v = d.objectifs?.[p.agent_id]?.[k];
              return <label key={k} className="block min-w-0"><span className="text-xs at-muted block mb-1">{l}</span><input key={`${k}-${v}`} type="number" min="0" defaultValue={v} onBlur={(e) => e.target.value !== "" && Number(e.target.value) !== v && api.patch(`/prospection/agents/${p.agent_id}`, { [k]: Number(e.target.value) }).then(() => { toast.success("Objectif enregistré."); charger(); }).catch((er) => toast.error(messageErreur(er)))} className="at-input" data-testid={`dist-goal-${k}-${p.agent_id}`} /></label>;
            })}
          </div>
          <ChoixListe label="Région préférée" valeur={d.preferences?.[p.agent_id] || "_"} onChange={(v) => api.patch(`/prospection/agents/${p.agent_id}`, { region_preferee: v === "_" ? "" : v }).then(charger)}
            options={[{ code: "_", label: "Aucune" }, ...(d.regions || []).map((r) => ({ code: r, label: r }))]} testId={`dist-region-${p.agent_id}`} />
        </div>
      ))}
      {d.absents.length > 0 && <p className="text-sm at-soft p-3" data-testid="dist-absents">Absents depuis 48 h (contacts rendus à la réserve) : {d.absents.map((a) => `${a.agent} (${a.contacts_rendus})`).join(", ")}</p>}
      <button type="button" className="at-btn at-btn-primary w-full" onClick={relancer} data-testid="dist-run"><RefreshCw size={16} />Lancer / relancer la distribution du jour</button>
    </div>
  );
};

const Regroupements = () => {
  const [liste, setListe] = useState([]);
  const charger = useCallback(() => api.get("/prospection/regroupements").then((r) => setListe(r.data)), []);
  useEffect(() => { charger(); }, [charger]);
  const detecter = () => api.post("/prospection/regroupements/detecter").then((r) => { toast.success(`${r.data.proposes} regroupement(s) proposé(s).`); charger(); });
  const traiter = (g, v) => api.post(`/prospection/regroupements/${g.id}`, { valider: v }).then(() => { toast.success(v ? "Regroupement validé." : "Regroupement rejeté."); charger(); });
  return (
    <div className="space-y-3" data-testid="groups-view">
      <button type="button" className="at-btn at-btn-ghost w-full" onClick={detecter} data-testid="groups-detect">Rechercher les propriétaires à plusieurs biens</button>
      {liste.length === 0 && <Vide texte="Aucun regroupement à valider." testId="groups-empty" />}
      {liste.map((g) => (
        <div key={g.id} className="at-card p-4 space-y-2" data-testid={`group-${g.id}`}>
          <p className="font-bold">{g.raison} · {g.biens.length} biens</p>
          {g.biens.map((b) => <p key={b.id} className="text-sm at-soft px-3 py-2 break-words">{[b.prenom, b.nom].filter(Boolean).join(" ")} · {b.telephone} · {[b.adresse, b.code_postal, b.ville].filter(Boolean).join(" ")}</p>)}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="at-btn at-btn-primary" onClick={() => traiter(g, true)} data-testid={`group-validate-${g.id}`}>Valider</button>
            <button type="button" className="at-btn at-btn-ghost" onClick={() => traiter(g, false)} data-testid={`group-reject-${g.id}`}>Rejeter</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Prospection() {
  const [onglet, setOnglet] = useState("fichiers");
  const [ouvert, setOuvert] = useState(false);
  const [cle, setCle] = useState(0);
  return (
    <div data-testid="page-prospection" className="min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="page-title">Fichiers de prospection</h1>
        <button type="button" className="at-btn at-btn-primary" onClick={() => setOuvert(true)} data-testid="prospect-import-open"><Upload size={16} />Importer un fichier</button>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {[["fichiers", "Fichiers"], ["distribution", "Distribution"], ["regroupements", "Regroupements à valider"]].map(([k, l]) => <Pill key={k} actif={onglet === k} onClick={() => setOnglet(k)} testId={`prospect-tab-${k}`}>{l}</Pill>)}
      </div>
      {onglet === "fichiers" && <Fichiers cle={cle} />}
      {onglet === "distribution" && <Distribution />}
      {onglet === "regroupements" && <Regroupements />}
      <ImportProspection ouvert={ouvert} setOuvert={setOuvert} onFini={() => setCle((c) => c + 1)} />
    </div>
  );
}
